import { useState } from "react";

import { type Floor } from "@eisox/api/core-api";
import { Select } from "@eisox/design-system";

import { RoomsSelector, type Filters } from "~/features/settings";
import { m } from "~/paraglide/messages";
import type { EnhancedRoom } from "~/types/room.type";

interface RoomsSelectProps extends Omit<Select.SelectProps<boolean>, "children"> {
  buildingId: string;
  floors: Floor[];
  rooms: EnhancedRoom[];
  handleRoomHovered?: (roomId: string | undefined) => void;
  side?: Select.ContentProps["side"];
  align?: Select.ContentProps["align"];
  disabled?: boolean;
  className?: string;
}

export const RoomsSelect: React.FC<RoomsSelectProps> = ({
  floors,
  value,
  onValueChange,
  multiple,
  rooms,
  handleRoomHovered,
  disabled = false,
  side,
  align,
  className,
}) => {
  const [filters, setFilters] = useState<Filters>({});
  const [filteredRooms, setFilteredRoms] = useState<EnhancedRoom[]>([]);

  const { textSearch, types, plans, groups } = filters;
  const unfiltered =
    (textSearch === undefined || textSearch === "") &&
    (types === undefined || types.length === 0) &&
    (plans === undefined || plans.length === 0) &&
    (groups === undefined || groups.length === 0);

  const displayOptions = unfiltered ? rooms : filteredRooms;

  return (
    <Select.Root multiple={multiple} value={value} onValueChange={onValueChange}>
      <Select.Trigger className={className} disabled={disabled}>
        <Select.Value placeholder={m.select_places({ resource: "room" })} />
      </Select.Trigger>
      <Select.Content className="w-105 max-w-105 min-w-105" side={side} align={align}>
        <RoomsSelector.Root
          rooms={rooms}
          value={filters}
          onValueChange={setFilters}
          onFilteredRoomsChange={setFilteredRoms}
        >
          <RoomsSelector.TextSearch />
          <div className="*:min-w-0 mt-2.5 mb-2.5 gap-2.5 flex *:flex-1">
            <RoomsSelector.TypeSearch />
            <RoomsSelector.PlanSearch floors={floors} />
            <RoomsSelector.GroupSearch />
          </div>
        </RoomsSelector.Root>
        <Select.Viewport>
          {displayOptions.map(room => (
            <Select.Item
              key={room.id}
              value={room.id}
              onMouseEnter={() => handleRoomHovered?.(room.id)}
              onMouseLeave={() => handleRoomHovered?.(undefined)}
            >
              {room.name} <span className="font-light text-sm text-primary-dark-gray italic">{room.floor.name}</span>
              <Select.ItemText>{room.name}</Select.ItemText>
            </Select.Item>
          ))}
        </Select.Viewport>
      </Select.Content>
    </Select.Root>
  );
};
